import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Breadcrumb, Layout, theme, Row, Card, Col, Tag, Form, Radio, Button, Modal, Select, Table, Descriptions,
    Input, Spin, Typography, Space, Tooltip
} from 'antd';
import { EyeFilled } from '@ant-design/icons';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import axios from 'axios';
import { AlertError, AlertSuccess } from '../usableclasses.js';
import ReactQuill from "react-quill";

const { Content } = Layout;
const { Title, Text } = Typography;

function StocksAdjustment() {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

    const [loading, setLoading] = useState(false);
    const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
    const [stockList, setStockList] = useState([]);
    const [stockItems, setStockItems] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [filterForm] = Form.useForm();
    const [productList, setProductList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [filterLoading, setFilterLoading] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [form] = Form.useForm();

    // Memoize year and month options so they are stable across renders
    const yearOptions = useMemo(() => (
        Array.from({ length: 10 }, (_, i) => {
            const year = new Date().getFullYear() - i;
            return { label: year.toString(), value: year.toString() };
        })
    ), []);

    const monthOptions = useMemo(() => (
        [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ].map((month, index) => ({ label: month, value: (index + 1).toString().padStart(2, '0') }))
    ), []);

    // Fetch stocks (stable callback)
    const fetchStocks = useCallback(async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/stocks/all`, { headers });
            setStockList(Array.isArray(res.data) ? res.data : []);
        } catch {
            AlertError("Failed to fetch stock list");
        }
    }, [ApiURL, TOKEN]);

    // Fetch stock items for a selected stock
    const fetchStockItems = useCallback(async (stockId) => {
        if (!stockId) {
            setStockItems([]);
            return;
        }
        setStockLoading(true);
        try {
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/stocks/stockitemslist/${stockId}`, { headers });
            setStockItems(Array.isArray(res.data) ? res.data : []);
        } catch {
            AlertError("Failed to fetch stock items");
        } finally {
            setStockLoading(false);
        }
    }, [ApiURL, TOKEN]);

    // Fetch products (stable)
    const fetchProducts = useCallback(async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/products/dropdown/all`, { headers });
            setProductList(Array.isArray(res.data) ? res.data : []);
        } catch {
            AlertError("Failed to fetch products");
        }
    }, [ApiURL, TOKEN]);

    // Create stock adjustment
    const handleCreateAdjustment = useCallback(async () => {
        try {
            const values = await form.validateFields();

            const selectedStockItem = stockItems.find(
                item => item.stockItemId === values.stockItemId
            );

            const payload = {
                stockItemId: values.stockItemId,
                stockId: values.stockId,
                productId: selectedStockItem?.itemId || 0,
                adjustmentType: values.adjustmentType,
                qty: values.qty,
                note: values.note,
                changedBy: UPDATED_BY,
            };

            setLoading(true);
            const headers = {
                SystemAccessToken: TOKEN,
                "Content-Type": "application/json",
            };

            const response = await axios.post(`${ApiURL}/stocks/stockadjustment`, payload, { headers });
            if (response?.status === 200 || response?.status === 201) {
                AlertSuccess("Adjustment created successfully!");
                form.resetFields();
                setStockItems([]);
                setAdjustmentModalOpen(false);
                // refresh filters / list
                handleFilter(1);
            } else {
                AlertError("Failed to create adjustment");
            }
        } catch (err) {
            if (err?.errorFields) {
                AlertError("Please fill all required fields");
            } else {
                AlertError("Failed to create adjustment");
            }
        } finally {
            setLoading(false);
        }
    }, [ApiURL, TOKEN, UPDATED_BY, form, stockItems]);

    // Filter handler (stable)
    const handleFilter = useCallback(async (page = 1) => {
        try {
            const values = await filterForm.validateFields().catch(() => ({}));
            const payload = {
                keyword: values.keyword || "",
                sysYear: values.sysYear || "",
                sysMonth: values.sysMonth || "",
                adjustType: values.adjustType || "",
                stockId: values.stockId || "",
                productId: values.productId || "",
                pageLength: pagination.pageSize,
                pageNumber: page
            };
            const headers = { SystemAccessToken: TOKEN };

            setFilterLoading(true);
            const res = await axios.post(`${ApiURL}/stocks/stocklistfilter`, payload, { headers });

            const list = Array.isArray(res.data?.dataList) ? res.data.dataList : [];
            const rowcount = res.data?.rowcount || 0;

            setFilteredData(list);
            setPagination(prev => ({ ...prev, current: page, total: rowcount }));
        } catch {
            AlertError("Failed to filter adjustments");
        } finally {
            setFilterLoading(false);
        }
    }, [ApiURL, TOKEN, filterForm, pagination.pageSize]);

    // Clear filter
    const clearFilter = useCallback(() => {
        filterForm.resetFields();
        handleFilter(1);
    }, [filterForm, handleFilter]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    }, []);

    // Columns memoized to avoid re-creation
    const columns = useMemo(() => [
        {
            title: "Type", dataIndex: "adjustmentType", key: "adjustmentType",
            render: (adjustmentType) => (
                <Tag color={adjustmentType === "ADD" ? "green" : "red"}>
                    {adjustmentType === "ADD" ? "ADD" : "MIN"}
                </Tag>
            ),
            sorter: (a, b) => (a.adjustmentType || '').localeCompare(b.adjustmentType || ''),
        },
        { title: "Stock Name", dataIndex: "stockName", key: "stockName", sorter: (a, b) => (a.stockName || '').localeCompare(b.stockName || '') },
        { title: "Product SKU", dataIndex: "productSku", key: "productSku", sorter: (a, b) => (a.productSku || '').localeCompare(b.productSku || '') },
        { title: "Product", dataIndex: "productName", key: "productName", sorter: (a, b) => (a.productName || '').localeCompare(b.productName || '') },
        { title: "Year", dataIndex: "sysYear", key: "sysYear", sorter: (a, b) => (a.sysYear || '').localeCompare(b.sysYear || '') },
        { title: "Month", dataIndex: "sysMonth", key: "sysMonth", sorter: (a, b) => (a.sysMonth || '').localeCompare(b.sysMonth || '') },
        {
            title: 'Create Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            render: (text) => formatDate(text),
            sorter: (a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0),
        },
        { title: "Qty", dataIndex: "adjustmentQty", key: "adjustmentQty", sorter: (a, b) => (String(a.adjustmentQty || '')).localeCompare(String(b.adjustmentQty || '')) },
        {
            title: "Action",
            key: "action",
            fixed: 'right',
            render: (_, record) => (
                <Tooltip title="View More">
                    <EyeFilled
                        style={{ fontSize: '18px', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => {
                            setSelectedRecord(record);
                            setViewModalVisible(true);
                        }}
                    />
                </Tooltip>
            )
        }
    ], [formatDate]);

    // Initial load: fetch stocks, products and initial filter
    useEffect(() => {
        fetchStocks();
        fetchProducts();
        // initial filter - ignore validation errors and use defaults
        handleFilter(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // keep empty to run once on mount

    // Table page change
    const onTableChange = useCallback((page) => {
        handleFilter(page);
    }, [handleFilter]);

    return (
        <Spin spinning={loading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Stocks</Breadcrumb.Item>
                        <Breadcrumb.Item>Stocks Adjustment</Breadcrumb.Item>
                    </Breadcrumb>

                    <div style={{ padding: 10, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
                        <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
                            <Row>
                                <Col xs={24} style={{ textAlign: "right", marginBottom: '10px' }}>
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            setAdjustmentModalOpen(true);
                                            fetchStocks();
                                            form.resetFields();
                                            setStockItems([]);
                                        }}
                                    >
                                        Create Adjustment
                                    </Button>
                                </Col>
                            </Row>
                            <Card title="Search Stocks Adjustments" style={{ marginBottom: 16 }}>
                                <Form
                                    form={filterForm}
                                    layout="vertical"
                                >
                                    <Row gutter={[16, 8]}>
                                        <Col xs={24} md={8}>
                                            <Form.Item name="keyword" label="Keyword">
                                                <Input placeholder="Enter keyword" style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item name="sysYear" label="Year">
                                                <Select placeholder="Select Year" style={{ width: "100%" }}>
                                                    {yearOptions.map(y => <Select.Option key={y.value} value={y.value}>{y.label}</Select.Option>)}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item name="sysMonth" label="Month">
                                                <Select placeholder="Select Month" style={{ width: "100%" }}>
                                                    {monthOptions.map(m => <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>)}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={4}>
                                            <Form.Item name="adjustType" label="Adjustment Type">
                                                <Select placeholder="Select Type" allowClear style={{ width: "100%" }}>
                                                    <Select.Option value="ADD">ADD</Select.Option>
                                                    <Select.Option value="MIN">MIN</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={[16, 8]} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Col xs={24} md={6}>
                                            <Form.Item name="stockId" label="Stock">
                                                <Select placeholder="Select Stock" style={{ width: "100%" }}>
                                                    {stockList.map((stock) => (
                                                        <Select.Option key={stock.stockId} value={stock.stockId}>
                                                            {stock.stockName}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item name="productId" label="Product">
                                                <Select placeholder="Select Product" style={{ width: "100%" }}>
                                                    {productList.map((product) => (
                                                        <Select.Option key={product.id} value={product.id}>
                                                            {product.label}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12} style={{ display: 'flex', alignItems: 'flex-end', }}>
                                            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                                <Form.Item>
                                                    <Button type="primary" onClick={() => handleFilter(1)} loading={filterLoading}>Filter</Button>
                                                </Form.Item>
                                                <Form.Item>
                                                    <Button onClick={clearFilter}>Clear</Button>
                                                </Form.Item>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card>
                            <Title level={5}>List of Adjustments</Title>
                            <Table
                                dataSource={filteredData}
                                loading={filterLoading}
                                pagination={{
                                    ...pagination,
                                    onChange: (page) => onTableChange(page),
                                    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
                                    showSizeChanger: false,
                                }}
                                rowKey="id"
                                columns={columns}
                                scroll={{ x: 'max-content' }}
                            />

                        </Space>
                    </div>
                </Content>

                <Footerdiv />

                <Modal
                    title="Create Stock Adjustment"
                    open={adjustmentModalOpen}
                    onCancel={() => setAdjustmentModalOpen(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setAdjustmentModalOpen(false)}>Cancel</Button>,
                        <Button key="create" type="primary" onClick={handleCreateAdjustment} loading={loading}>Create</Button>,
                    ]}
                >
                    <Form layout="vertical" form={form}>
                        <Form.Item
                            label="Select Stock"
                            name="stockId"
                            rules={[{ required: true, message: "Please select a stock" }]}
                        >
                            <Select
                                placeholder="Select Stock"
                                onChange={(val) => {
                                    form.setFieldsValue({ stockItemId: undefined });
                                    fetchStockItems(val);
                                }}
                            >
                                {stockList.map((stock) => (
                                    <Select.Option key={stock.stockId} value={stock.stockId}>
                                        {stock.stockName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Select Stock Item"
                            name="stockItemId"
                            rules={[{ required: true, message: "Please select a stock item" }]}
                        >
                            <Select
                                placeholder="Select Stock Item"
                                disabled={!form.getFieldValue("stockId")}
                                loading={stockLoading}
                            >
                                {stockItems.map((item) => (
                                    <Select.Option key={item.stockItemId} value={item.stockItemId}>
                                        {item.itemName} ({item.itemSku})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Adjustment Type"
                            name="adjustmentType"
                            initialValue="ADD"
                            rules={[{ required: true }]}
                        >
                            <Radio.Group>
                                <Radio value="ADD">Add</Radio>
                                <Radio value="MIN">Minus</Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            label="Adjustment Quantity"
                            name="qty"
                            rules={[{ required: true, message: "Please enter quantity" }]}
                        >
                            <Input type="number" min={1} placeholder="Enter quantity" />
                        </Form.Item>

                        <Form.Item
                            label="Adjustment Note"
                            name="note"
                            rules={[{ required: true, message: "Please enter a note" }]}
                        >
                            <ReactQuill theme="snow" placeholder="Enter your description..." />
                        </Form.Item>
                    </Form>
                </Modal>
                <Modal
                    title="Stock Adjustment Details"
                    open={viewModalVisible}
                    onCancel={() => setViewModalVisible(false)}
                    footer={null}
                    width={700}
                >
                    {selectedRecord ? (
                        <Descriptions
                            bordered
                            column={1}
                            size="middle"
                            labelStyle={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                        >
                            <Descriptions.Item label="Stock Name">{selectedRecord.stockName}</Descriptions.Item>
                            <Descriptions.Item label="Product SKU">{selectedRecord.productSku}</Descriptions.Item>
                            <Descriptions.Item label="Product Name">{selectedRecord.productName}</Descriptions.Item>
                            <Descriptions.Item label="Adjustment Type">{selectedRecord.adjustmentType}</Descriptions.Item>
                            <Descriptions.Item label="Financial Year">{selectedRecord.finYear}</Descriptions.Item>
                            <Descriptions.Item label="System Year">{selectedRecord.sysYear}</Descriptions.Item>
                            <Descriptions.Item label="System Month">{selectedRecord.sysMonth}</Descriptions.Item>
                            <Descriptions.Item label="Qty Adjusted">
                                <Text type={Number(selectedRecord.adjustmentQty) > 0 ? 'success' : 'danger'}>
                                    {selectedRecord.adjustmentQty}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Adjustment Note">
                                <div
                                    dangerouslySetInnerHTML={{ __html: selectedRecord.adjustmentNote }}
                                    style={{ background: '#fafafa', padding: '10px', borderRadius: '5px' }}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Created By">{selectedRecord.createdBy}</Descriptions.Item>
                            <Descriptions.Item label="Created Date">{selectedRecord.createdDate}</Descriptions.Item>
                        </Descriptions>
                    ) : (
                        <Spin spinning />
                    )}
                </Modal>

            </Layout>
        </Spin>
    );
}

export default StocksAdjustment;